import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Button, Table, Input, FormGroup, Label } from 'reactstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import {
  newFetchTeacherByCourseId,
  transactionCourseChange,
  changeLevel,
} from '../../../../../Api/apiUtils';
import { PaymentConfigContext } from '../../../../../Api/global/ApiContext';
import { MixpanelEvents, trackEvent } from '../../../../../utils/mixpanelTracking';
import {
  calculateTotalPrice,
  buildToCourseDetail,
  buildSourcePayload,
  buildRemoveLevels,
  combineTransactions,
  firstFilledLevelId,
  changeErrorMessage,
  reportActiveLevelFailure,
  maxPurchasableClasses,
  reportChangeResult,
  sumRemainingClasses,
  sumRemainingValue,
  selectMenuPortalProps,
} from './txnChangeShared';
import TeacherSelect from './TeacherSelect';

export interface PanelProps {
  /** The selected transactions — all of the same course. */
  transactions: any[];
  studentId: string;
  profileData: any;
  userType?: string;
  onDone: () => void;
}

// Popup body for "Change Course": move the selected transactions' remaining classes to a
// DIFFERENT course in one change.
const CourseChangePanel: React.FC<PanelProps> = ({ transactions, studentId, profileData, userType, onDone }) => {
  const transaction = useMemo(() => combineTransactions(transactions), [transactions]);
  const paymentConfig = useContext(PaymentConfigContext);
  const paymentCourses: any[] = paymentConfig?.onlineCourseAcademic2 || [];

  const [submitting, setSubmitting] = useState(false);
  const [selectedPaymentCourse, setSelectedPaymentCourse] = useState<{ value: string; label: string } | null>(null);
  const [toCourseDetail, setToCourseDetail] = useState<any>(null);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<any>('');

  useEffect(() => {
    if (!selectedPaymentCourse) return;
    (async () => {
      try {
        const response = await newFetchTeacherByCourseId({ page: 0, size: 500, courseId: selectedPaymentCourse.value });
        setTeachers(Array.isArray(response?.data?.content) ? response.data.content : []);
      } catch (e) {
        setTeachers([]);
      }
    })();
  }, [selectedPaymentCourse]);

  const availableAmount = +sumRemainingValue(transaction).toFixed(2);
  const selectedLevelId = firstFilledLevelId(toCourseDetail);
  const activeLevelName = toCourseDetail?.levelList?.find((l: any) => l.levelId === selectedLevelId)?.level || '';
  const remainingClasses = sumRemainingClasses(transaction);
  const newCourseValue = toCourseDetail ? +(+toCourseDetail.discountedTotal).toFixed(2) : 0;
  const walletAmount = Math.max(0, +(availableAmount - newCourseValue).toFixed(2));

  const handlePaymentCourseChange = async (option: { value: string; label: string } | null) => {
    setSelectedPaymentCourse(option);
    setSelectedTeacher('');
    if (!option) {
      setToCourseDetail(null);
      return;
    }
    try {
      const detail = await buildToCourseDetail(paymentCourses, option.value, paymentConfig, profileData?.timezone);
      setToCourseDetail(detail);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to fetch course price');
      setToCourseDetail(null);
    }
  };

  const handleClassCount = (levelId: string, value: string) => {
    const sanitized = value.replace(/[^0-9]/g, '');
    const newLevelList = toCourseDetail.levelList.map((l: any) =>
      l.levelId === levelId ? { ...l, classCount: +sanitized } : l
    );
    setToCourseDetail(calculateTotalPrice({ ...toCourseDetail, levelList: newLevelList }));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!selectedPaymentCourse || !toCourseDetail) return toast.error('Select the new course');
    if (!selectedTeacher?.value) return toast.error('Select a teacher');
    if (newCourseValue <= 0) return toast.error('Add at least one class to the new course');
    if (newCourseValue > availableAmount + 0.5) {
      return toast.error('New course value exceeds the available amount (partial top-up is not allowed)');
    }

    const removeLevels = buildRemoveLevels(transactions);

    const addLevels = toCourseDetail.levelList
      .filter((l: any) => l.classCount !== '' && +l.classCount !== 0)
      .map((l: any) => {
        const discount = +((+l.unit_price * +l.classCount * toCourseDetail.discountPercentage) / 100).toFixed(2);
        const finalAmount = +((+l.unit_price * +l.classCount) - discount).toFixed(2);
        return {
          level: l.level,
          levelId: l.levelId,
          courseId: l.courseId,
          courseName: l.courseName,
          noOfClasses: +l.classCount,
          unit_price: l.unit_price,
          totalDiscount: discount,
          totalFinalPrice: finalAmount,
        };
      });

    const fromCourseId = transaction.courseList?.[0]?.courseId;
    const Userdetails = JSON.parse(localStorage.getItem('profileData') || '{}');

    const payload = {
      studentId,
      userId: profileData?.userId,
      name: profileData?.firstName || transaction.name,
      email: transaction.email || profileData?.emailId,
      currency: transaction.currency || toCourseDetail.currency,
      customer_id: transaction.customer_id,
      centreId: transaction.centreId || '',
      place_of_supply: transaction.place_of_supply || '',
      userType: userType || transaction.studentType || 'PAID',
      conversionRate: transaction.conversionRate,
      changedBy: Userdetails?.firstName || '',
      isFreeClass: false,
      teacherId: selectedTeacher.value,
      activeLevelId: selectedLevelId,
      activeLevel: activeLevelName,
      remainingClasses,
      remainingValue: availableAmount,
      newCourseValue,
      walletAmount,
      fromCourse: {
        courseId: fromCourseId,
        courseName: transaction.courseList?.[0]?.courseName || '',
        removeLevels,
      },
      toCourse: {
        courseId: selectedPaymentCourse.value,
        courseName: toCourseDetail.levelList?.[0]?.courseName || '',
        addLevels,
      },
      sources: transactions.map(buildSourcePayload),
    };

    setSubmitting(true);
    try {
      const response = await transactionCourseChange(payload);
      if (!response || response.success === false) {
        toast.error('Failed to change course');
        trackEvent(MixpanelEvents.COURSE_CHANGE, {
          page: 'Transaction Course Change', studentId, userId: profileData?.userId,
          previousCourseId: fromCourseId, courseId: selectedPaymentCourse.value, status: 'failed',
        });
        return;
      }
      try {
        await changeLevel({
          userId: profileData?.userId,
          courseId: selectedPaymentCourse.value,
          levelId: selectedLevelId,
          changeType: 'course',
          previousCourseId: fromCourseId,
        });
      } catch (e) {
        console.error('changeLevel failed', response?.traceId, e);
        reportActiveLevelFailure(response, activeLevelName);
      }
      trackEvent(MixpanelEvents.COURSE_CHANGE, {
        page: 'Transaction Course Change', studentId, userId: profileData?.userId,
        previousCourseId: fromCourseId, courseId: selectedPaymentCourse.value,
        newTransactionId: response.newTransactionId, walletAmount, status: 'success',
      });
      reportChangeResult(response, 'Course changed successfully!');
      onDone();
    } catch (error: any) {
      console.error('transactionCourseChange error', error);
      toast.error(changeErrorMessage(error, 'Failed to change course'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h6>Available amount: <b>{availableAmount.toFixed(2)} {transaction.currency}</b> ({remainingClasses} classes)</h6>
      <FormGroup>
        <Label>New Course</Label>
        <Select
          {...selectMenuPortalProps}
          value={selectedPaymentCourse}
          onChange={(v: any) => handlePaymentCourseChange(v)}
          options={paymentCourses
            .filter((c) => String(c.paidCourseId) !== String(transaction.courseList?.[0]?.courseId))
            .map((c) => ({ value: c.paidCourseId, label: c.name }))}
          placeholder="Select a course"
        />
      </FormGroup>

      {toCourseDetail && (
        <>
          <Table bordered>
            <thead>
              <tr>
                <th>Level</th>
                <th>Class Price</th>
                <th title="Most classes of this level that still fit in the available amount, after the classes entered for the other levels">Max Affordable Classes</th>
                <th>Class Count</th>
              </tr>
            </thead>
            <tbody>
              {toCourseDetail.levelList.map((l: any) => (
                <tr key={l.levelId}>
                  <td>{l.level}</td>
                  <td>{l.unit_price}</td>
                  <td>{maxPurchasableClasses(toCourseDetail, l.levelId, availableAmount)}</td>
                  <td style={{ width: 120 }}>
                    <Input type="text" value={l.classCount} onChange={(e) => handleClassCount(l.levelId, e.target.value)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <FormGroup>
            <Label>Teacher</Label>
            <TeacherSelect teachers={teachers} value={selectedTeacher} onChange={setSelectedTeacher} />
          </FormGroup>

          <div style={{ marginTop: 10, padding: 10, background: '#f7f7f7', borderRadius: 8 }}>
            <div>New course value: <b>{newCourseValue.toFixed(2)} {toCourseDetail.currency}</b></div>
            <div>Active level (first level with classes): <b>{activeLevelName || '-'}</b></div>
            <div>Wallet credit (leftover): <b>{walletAmount.toFixed(2)} {toCourseDetail.currency}</b></div>
            {newCourseValue > availableAmount + 0.5 && (
              <div style={{ color: 'red' }}>New course value exceeds available amount — not allowed.</div>
            )}
          </div>

          <Button
            color="primary"
            style={{ marginTop: 15 }}
            disabled={submitting || newCourseValue <= 0 || newCourseValue > availableAmount + 0.5}
            onClick={handleSubmit}
          >
            {submitting ? 'Processing…' : 'Change Course'}
          </Button>
        </>
      )}
    </div>
  );
};

export default CourseChangePanel;
